import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import LandingAlternative from "@/pages/LandingAlternative";
import SignUp from "@/pages/SignUp";
import SignIn from "@/pages/SignIn";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import LoginSupport from "@/pages/LoginSupport";
import UpdatePassword from "@/pages/UpdatePassword";
import VerifyMfa from "@/pages/VerifyMfa";
import SecureAccount from "@/pages/SecureAccount";
import Unsubscribe from "@/pages/Unsubscribe";
import Onboarding from "@/pages/Onboarding";
import AdventureDetail from "@/pages/AdventureDetail";
import AdventureChatView from "@/pages/AdventureChatView";
import CreateAdventure from "@/pages/CreateAdventure";
import Kinfeed from "@/pages/Kinfeed";
import Communities from "@/pages/Communities";
import Activities from "@/pages/Activities";
import ChannelView from "@/pages/ChannelView";
import ReplyThread from "@/pages/ReplyThread";
import LocalMembers from "@/pages/LocalMembers";
import CommunityLocalHub from "@/pages/CommunityLocalHub";
import MemberProfile from "@/pages/MemberProfile";
import MessageThread from "@/pages/MessageThread";
import Notifications from "@/pages/Notifications";
import Support from "@/pages/Support";
import SupportKindred from "@/pages/SupportKindred";
import SupportCategory from "@/pages/SupportCategory";
import HelperPack from "@/pages/HelperPack";
import EventDetail from "@/pages/EventDetail";
import CreateEvent from "@/pages/CreateEvent";
import MyKindred from "@/pages/MyKindred";
import MyAdventures from "@/pages/MyAdventures";
import Settings from "@/pages/Settings";
import DeleteAccount from "@/pages/DeleteAccount";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMembers from "@/pages/AdminMembers";
import AdminMemberDetail from "@/pages/AdminMemberDetail";
import AdminContent from "@/pages/AdminContent";
import AdminActivities from "@/pages/AdminActivities";
import AdminEvents from "@/pages/AdminEvents";
import AdminModeration from "@/pages/AdminModeration";
import AdminAccountDeletion from "@/pages/AdminAccountDeletion";
import AdminLoginSupport from "@/pages/AdminLoginSupport";
import ModerationGuidance from "@/pages/ModerationGuidance";
import Privacy from "@/pages/Privacy";
import PrivacyFrench from "@/pages/PrivacyFrench";
import Terms from "@/pages/Terms";
import TermsFrench from "@/pages/TermsFrench";
import CodeOfConduct from "@/pages/CodeOfConduct";
import ModerationGuidelinesPage from "@/pages/ModerationGuidelinesPage";
import EditorialPolicy from "@/pages/EditorialPolicy";
import ChildSafety from "@/pages/ChildSafety";
import AboutKindred from "@/pages/AboutKindred";
import DataRights from "@/pages/DataRights";
import ContactComplaints from "@/pages/ContactComplaints";
import ComposePost from "@/pages/ComposePost";
import FeedPostDetail from "@/pages/FeedPostDetail";
import CreateChatRoom from "@/pages/CreateChatRoom";
import ChatRoomView from "@/pages/ChatRoomView";
import CampingBreaksInvite from "@/pages/CampingBreaksInvite";
import NotFound from "@/pages/NotFound";
import { useEffect } from "react";
import { trackKindredScreenView } from "@/lib/productAnalytics";
import { syncWebSentryUser } from "@/lib/sentry";
import {
  buildWebAuthPath,
  clearWebPostAuthRedirectIfCurrent,
  peekWebPostAuthRedirect,
  rememberWebPostAuthRedirect,
} from "@/lib/postAuthRedirect";
import { isCampingBreaksRoom } from "../shared/community/campaigns";

const queryClient = new QueryClient();

const AuthDestinationRedirect = ({
  target,
  authPath = "/signin",
}: {
  target: string;
  authPath?: "/signin" | "/onboarding";
}) => {
  useEffect(() => {
    rememberWebPostAuthRedirect(target);
  }, [target]);

  return (
    <Navigate
      to={authPath === "/signin" ? buildWebAuthPath("/signin", target) : authPath}
      replace
    />
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, securityLoading, requiresMfa, requiresPrivilegedMfaSetup, onboardingComplete } = useAuth();
  const location = useLocation();
  const currentTarget = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (user && onboardingComplete === true) {
      clearWebPostAuthRedirectIfCurrent(currentTarget);
    }
  }, [currentTarget, onboardingComplete, user]);

  if (loading || securityLoading || onboardingComplete === null && user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <AuthDestinationRedirect target={currentTarget} />;

  if (requiresPrivilegedMfaSetup && window.location.pathname !== "/secure-account") {
    return <Navigate to="/secure-account" replace />;
  }

  if (requiresMfa && window.location.pathname !== "/verify-2fa") {
    return <Navigate to="/verify-2fa" replace />;
  }

  if (!onboardingComplete && window.location.pathname !== "/onboarding") {
    return <AuthDestinationRedirect target={currentTarget} authPath="/onboarding" />;
  }

  return <>{children}</>;
};

const MemberShellRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <AppShell>{children}</AppShell>
  </ProtectedRoute>
);

const MemberShellWhenReady = ({ children }: { children: React.ReactNode }) => {
  const { user, onboardingComplete, requiresMfa, requiresPrivilegedMfaSetup } = useAuth();

  if (user && onboardingComplete && !requiresMfa && !requiresPrivilegedMfaSetup) {
    return <AppShell>{children}</AppShell>;
  }

  return <>{children}</>;
};

const PrivilegedRoute = ({ children }: { children: React.ReactNode }) => {
  const {
    role,
    user,
    loading,
    securityLoading,
    requiresMfa,
    requiresPrivilegedMfaSetup,
    onboardingComplete,
  } = useAuth();

  if (loading || securityLoading || (onboardingComplete === null && user)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;
  if (requiresPrivilegedMfaSetup && window.location.pathname !== "/secure-account") {
    return <Navigate to="/secure-account" replace />;
  }
  if (requiresMfa && window.location.pathname !== "/verify-2fa") {
    return <Navigate to="/verify-2fa" replace />;
  }
  if (!onboardingComplete) return <Navigate to="/onboarding" replace />;
  if (role !== "admin" && role !== "moderator") {
    return <Navigate to="/my-kindred" replace />;
  }

  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const {
    role,
    user,
    loading,
    securityLoading,
    requiresMfa,
    requiresPrivilegedMfaSetup,
    onboardingComplete,
  } = useAuth();

  if (loading || securityLoading || (onboardingComplete === null && user)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;
  if (requiresPrivilegedMfaSetup && window.location.pathname !== "/secure-account") {
    return <Navigate to="/secure-account" replace />;
  }
  if (requiresMfa && window.location.pathname !== "/verify-2fa") {
    return <Navigate to="/verify-2fa" replace />;
  }
  if (!onboardingComplete) return <Navigate to="/onboarding" replace />;
  if (role !== "admin") {
    return <Navigate to="/my-kindred" replace />;
  }

  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, securityLoading, requiresMfa, requiresPrivilegedMfaSetup, onboardingComplete } = useAuth();
  if (loading || securityLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (user) {
    if (requiresPrivilegedMfaSetup) return <Navigate to="/secure-account" replace />;
    if (requiresMfa) return <Navigate to="/verify-2fa" replace />;
    if (onboardingComplete === null) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      );
    }
    if (!onboardingComplete) return <Navigate to="/onboarding" replace />;
    return <Navigate to={peekWebPostAuthRedirect() ?? "/kinfeed"} replace />;
  }
  return <>{children}</>;
};

const ChatRoomRoute = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { user, loading, securityLoading } = useAuth();

  if (loading || securityLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user && isCampingBreaksRoom(roomId)) {
    return <CampingBreaksInvite />;
  }

  return <MemberShellRoute><ChatRoomView /></MemberShellRoute>;
};

const SessionRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, securityLoading } = useAuth();

  if (loading || securityLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/signin" replace />;

  return <>{children}</>;
};

const WebRouteAnalytics = () => {
  const location = useLocation();
  const { user, loading, securityLoading } = useAuth();

  useEffect(() => {
    syncWebSentryUser(user ? { id: user.id, email: user.email ?? null } : null);
  }, [user?.email, user?.id]);

  useEffect(() => {
    if (loading || securityLoading || !user?.id) return;
    void trackKindredScreenView(user.id, location.pathname);
  }, [location.pathname, loading, securityLoading, user?.id]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <WebRouteAnalytics />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/landing" element={<LandingAlternative />} />
            <Route path="/pricing" element={<Navigate to="/landing" replace />} />
            <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
            <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
            <Route path="/auth" element={<AuthCallback />} />
            <Route path="/privacy" element={<MemberShellWhenReady><Privacy /></MemberShellWhenReady>} />
            <Route path="/privacy-fr" element={<MemberShellWhenReady><PrivacyFrench /></MemberShellWhenReady>} />
            <Route path="/terms" element={<MemberShellWhenReady><Terms /></MemberShellWhenReady>} />
            <Route path="/terms-fr" element={<MemberShellWhenReady><TermsFrench /></MemberShellWhenReady>} />
            <Route path="/conduct" element={<MemberShellWhenReady><CodeOfConduct /></MemberShellWhenReady>} />
            <Route path="/about-kindred" element={<MemberShellWhenReady><AboutKindred /></MemberShellWhenReady>} />
            <Route path="/data-rights" element={<MemberShellWhenReady><DataRights /></MemberShellWhenReady>} />
            <Route path="/contact-complaints" element={<MemberShellWhenReady><ContactComplaints /></MemberShellWhenReady>} />
            <Route path="/editorial-policy" element={<MemberShellWhenReady><EditorialPolicy /></MemberShellWhenReady>} />
            <Route path="/child-safety" element={<MemberShellWhenReady><ChildSafety /></MemberShellWhenReady>} />
            <Route path="/moderation-guidelines" element={<MemberShellRoute><PrivilegedRoute><ModerationGuidelinesPage /></PrivilegedRoute></MemberShellRoute>} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login-support" element={<LoginSupport />} />
            <Route path="/update-password" element={<UpdatePassword />} />
            <Route path="/secure-account" element={<SessionRoute><SecureAccount /></SessionRoute>} />
            <Route path="/verify-2fa" element={<SessionRoute><VerifyMfa /></SessionRoute>} />

            {/* Onboarding */}
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

            {/* Full-screen protected routes */}
            <Route path="/adventures/new" element={<MemberShellRoute><CreateAdventure mode="create" /></MemberShellRoute>} />
            <Route path="/adventures/edit/:id" element={<MemberShellRoute><CreateAdventure mode="edit" /></MemberShellRoute>} />
            <Route path="/adventures/duplicate/:id" element={<MemberShellRoute><CreateAdventure mode="duplicate" /></MemberShellRoute>} />
            <Route path="/adventures/:id/chat" element={<MemberShellRoute><AdventureChatView /></MemberShellRoute>} />
            <Route path="/adventures/:id" element={<MemberShellRoute><AdventureDetail /></MemberShellRoute>} />
            <Route path="/community/channel/:id" element={<MemberShellRoute><ChannelView /></MemberShellRoute>} />
            <Route path="/community/channel/:channelId/post/:postId" element={<MemberShellRoute><ReplyThread /></MemberShellRoute>} />
            <Route path="/community/members" element={<MemberShellRoute><LocalMembers /></MemberShellRoute>} />
            <Route path="/community/local" element={<MemberShellRoute><CommunityLocalHub /></MemberShellRoute>} />
            <Route path="/community/member/:id" element={<MemberShellRoute><MemberProfile /></MemberShellRoute>} />
            <Route path="/community/feed/new" element={<MemberShellRoute><ComposePost /></MemberShellRoute>} />
            <Route path="/community/feed/:postId" element={<MemberShellRoute><FeedPostDetail /></MemberShellRoute>} />
            <Route path="/community/room/new" element={<MemberShellRoute><CreateChatRoom /></MemberShellRoute>} />
            <Route path="/community/room/:roomId" element={<ChatRoomRoute />} />
            <Route path="/messages" element={<MemberShellRoute><Navigate to="/communities?section=messages" replace /></MemberShellRoute>} />
            <Route path="/messages/:connectionId" element={<MemberShellRoute><MessageThread /></MemberShellRoute>} />
            <Route path="/notifications" element={<MemberShellRoute><Notifications /></MemberShellRoute>} />
            <Route path="/whats-on/:id" element={<MemberShellRoute><EventDetail /></MemberShellRoute>} />
            <Route path="/whats-on/new" element={<MemberShellRoute><CreateEvent /></MemberShellRoute>} />
            <Route path="/whats-on/edit/:id" element={<MemberShellRoute><Navigate to="/activities?section=whats-on" replace /></MemberShellRoute>} />
            <Route path="/admin" element={<MemberShellRoute><AdminRoute><AdminDashboard /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/activities" element={<MemberShellRoute><AdminRoute><AdminActivities /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/members" element={<MemberShellRoute><AdminRoute><AdminMembers /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/members/:id" element={<MemberShellRoute><AdminRoute><AdminMemberDetail /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/content" element={<MemberShellRoute><AdminRoute><AdminContent /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/events" element={<MemberShellRoute><AdminRoute><AdminEvents /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/moderation" element={<MemberShellRoute><PrivilegedRoute><AdminModeration /></PrivilegedRoute></MemberShellRoute>} />
            <Route path="/admin/account-deletion" element={<MemberShellRoute><AdminRoute><AdminAccountDeletion /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/login-support" element={<MemberShellRoute><AdminRoute><AdminLoginSupport /></AdminRoute></MemberShellRoute>} />
            <Route path="/admin/guidance" element={<MemberShellRoute><PrivilegedRoute><ModerationGuidance /></PrivilegedRoute></MemberShellRoute>} />

            {/* App shell routes */}
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="kinfeed" element={<Kinfeed />} />
              <Route path="communities" element={<Communities />} />
              <Route path="activities" element={<Activities />} />
              <Route path="community" element={<Navigate to="/kinfeed" replace />} />
              <Route path="adventures" element={<Navigate to="/activities" replace />} />
              <Route path="whats-on" element={<Navigate to="/activities?section=whats-on" replace />} />
              <Route path="support" element={<Support />} />
              <Route path="support/kindred" element={<SupportKindred />} />
              <Route path="support/:slug" element={<SupportCategory />} />
              <Route path="support/pack/:slug" element={<HelperPack />} />
              <Route path="my-kindred" element={<MyKindred />} />
              <Route path="my-adventures" element={<MyAdventures />} />
              <Route path="settings" element={<Settings />} />
              <Route path="my-kindred/delete-account" element={<DeleteAccount />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
